import { fromEntries } from "../utils/browserCompat";
import { createTheme } from "@mui/material/styles";

const FALLBACK_BODY = '"Asap", "Segoe UI", sans-serif';
const FALLBACK_HEADING = '"Exo 2", "Asap", "Segoe UI", sans-serif';
const COLOR_PATTERN = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[0-9.]+%?\s*,\s*[0-9.]+%?\s*,\s*[0-9.]+%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|hsla?\(\s*[0-9.]+(?:deg)?\s*,\s*[0-9.]+%\s*,\s*[0-9.]+%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|transparent|currentcolor)$/i;
const AUGMENTABLE_COLOR_PATTERN = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[0-9.]+%?\s*,\s*[0-9.]+%?\s*,\s*[0-9.]+%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|hsla?\(\s*[0-9.]+(?:deg)?\s*,\s*[0-9.]+%\s*,\s*[0-9.]+%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\))$/i;

const STOREFRONT_TYPOGRAPHY_DENSITY = 0.9;
const STOREFRONT_SPACING_DENSITY = 0.88;

const RENDER_DEFAULTS = Object.freeze({
  typography: Object.freeze({
    baseFontSize: 16,
    scaleRatio: 1.2,
    lineHeight: 1.5,
    headingLineHeight: 1.15,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 800,
    letterSpacing: 0,
  }),
  spacing: Object.freeze({
    unit: 8,
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    section: 72,
  }),
  shadows: Object.freeze({
    sm: "0 8px 20px rgba(20,22,40,.14)",
    md: "6px 6px 16px rgba(39,42,64,.12), -6px -6px 16px rgba(255,255,255,.85)",
    lg: "0 12px 28px rgba(20,22,40,.2)",
  }),
});

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function objectOrEmpty(value) {
  return isPlainObject(value) ? value : {};
}

function finiteNumber(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

function normalizeColorAlias(value) {
  const candidate = String(value ?? "").trim().slice(0, 100);
  const lowered = candidate.toLowerCase();
  if (["transparent", "transparente", "trasparente"].includes(lowered)) return "transparent";
  return candidate;
}

function safeColor(value, fallback, { augmentable = false } = {}) {
  const candidate = normalizeColorAlias(value);
  const pattern = augmentable ? AUGMENTABLE_COLOR_PATTERN : COLOR_PATTERN;
  return pattern.test(candidate) ? candidate : fallback;
}

function colorWithAlpha(value, alphaHex) {
  const candidate = normalizeColorAlias(value);
  if (!candidate || candidate === "transparent") return "transparent";
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return `${candidate}${alphaHex}`;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    const expanded = `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
    return `${expanded}${alphaHex}`;
  }
  return candidate;
}

function muiPaletteMain(value, fallback) {
  return safeColor(value, fallback, { augmentable: true });
}

function safeFontFamily(value, fallback) {
  const candidate = String(value ?? "").trim().slice(0, 140);
  if (!candidate || /[\u0000-\u001f\u007f{};<>"'\\]/.test(candidate)) return fallback;
  return candidate;
}

function fontStack(value, fallback) {
  const family = safeFontFamily(value, "");
  if (!family) return fallback;
  if (family.includes(",")) return family;
  const quoted = /[\s]/.test(family) ? `"${family}"` : family;
  return `${quoted}, ${fallback}`;
}

function pxToRem(value) {
  return `${value / 16}rem`;
}

function scaledHeadingSize(baseFontSize, scaleRatio, exponent, min, max) {
  return pxToRem(
    finiteNumber(baseFontSize * scaleRatio ** exponent, min, min, max)
  );
}

function storefrontVisualDefaults(landingTheme = {}) {
  const source = objectOrEmpty(landingTheme);
  const primary = safeColor(source.primaryColor, "#E44E31");
  const secondary = safeColor(source.secondaryColor, "#494A98");
  const divider = safeColor(source.dividerColor, "rgba(26,26,46,.09)");
  const radius = finiteNumber(source.borderRadius, 10, 0, 80);

  return {
    colors: {
      primary,
      secondary,
      background: safeColor(source.backgroundColor, "#EEF0F5"),
      surface: safeColor(source.surfaceColor, "#F7F8FC"),
      text: safeColor(source.textColor, "#1A1A2E"),
      mutedText: safeColor(source.mutedTextColor, "#5B5D73"),
      divider,
      onPrimary: "#FFFFFF",
      onSecondary: "#FFFFFF",
      success: "#2E7D32",
      warning: "#F57C00",
      error: "#D0333A",
    },
    typography: {
      ...RENDER_DEFAULTS.typography,
      fontFamily: safeFontFamily(source.fontFamily, "Asap"),
      headingFontFamily: safeFontFamily(source.headingFontFamily || source.fontFamily, "Exo 2"),
      bodyFontFamily: safeFontFamily(source.bodyFontFamily || source.fontFamily, "Asap"),
    },
    spacing: RENDER_DEFAULTS.spacing,
    borders: {
      width: 1,
      style: "solid",
      radiusSm: Math.max(8, radius),
      radiusMd: radius,
      radiusLg: Math.max(14, radius + 4),
      radiusPill: 9999,
      color: divider,
    },
    shadows: {
      ...RENDER_DEFAULTS.shadows,
      focus: `0 0 0 4px ${colorWithAlpha(primary, "18")}`,
    },
  };
}

export function createStorefrontTheme(landingTheme = {}) {
  const source = objectOrEmpty(landingTheme);
  const defaults = storefrontVisualDefaults(source);
  const { colors, typography, spacing, borders, shadows } = defaults;

  const bodyFamily = fontStack(
    typography.bodyFontFamily || typography.fontFamily,
    FALLBACK_BODY
  );
  const headingFamily = fontStack(
    typography.headingFontFamily || typography.fontFamily,
    FALLBACK_HEADING
  );
  const baseFontSize = typography.baseFontSize * STOREFRONT_TYPOGRAPHY_DENSITY;
  const renderSpacing = fromEntries(
    Object.entries(spacing).map(([key, value]) => [
      key,
      Number(value) * STOREFRONT_SPACING_DENSITY,
    ])
  );
  const scaleRatio = typography.scaleRatio;
  const letterSpacing = `${typography.letterSpacing}em`;
  const buttonPaddingY = finiteNumber(renderSpacing.sm, 7, 4, 18);
  const buttonPaddingX = finiteNumber(renderSpacing.lg, 21, 10, 42);
  const buttonMinHeight = finiteNumber(
    baseFontSize * typography.lineHeight + buttonPaddingY * 2,
    38,
    34,
    64
  );
  const borderRule = `${borders.width}px ${borders.style} ${borders.color}`;

  return createTheme({
    palette: {
      mode: source.mode === "dark" ? "dark" : "light",
      primary: { main: muiPaletteMain(colors.primary, "#E44E31"), contrastText: muiPaletteMain(colors.onPrimary, "#FFFFFF") },
      secondary: { main: muiPaletteMain(colors.secondary, "#494A98"), contrastText: muiPaletteMain(colors.onSecondary, "#FFFFFF") },
      success: { main: muiPaletteMain(colors.success, "#2E7D32") },
      error: { main: muiPaletteMain(colors.error, "#D0333A") },
      warning: { main: muiPaletteMain(colors.warning, "#F57C00") },
      info: { main: "#55ACEE" },
      mp: { main: "#00A3E0" },
      white: { main: "#fff" },
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.text, secondary: colors.mutedText },
      divider: colors.divider,
    },
    spacing: renderSpacing.unit,
    typography: {
      htmlFontSize: 16,
      fontSize: baseFontSize,
      fontFamily: bodyFamily,
      fontWeightRegular: typography.fontWeightRegular,
      fontWeightMedium: typography.fontWeightMedium,
      fontWeightBold: typography.fontWeightBold,
      body1: {
        fontFamily: bodyFamily,
        fontSize: pxToRem(baseFontSize),
        lineHeight: typography.lineHeight,
        letterSpacing,
      },
      body2: {
        fontFamily: bodyFamily,
        fontSize: pxToRem(baseFontSize * 0.875),
        lineHeight: typography.lineHeight,
        letterSpacing,
      },
      h1: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 7, 32, 96),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      h2: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 6, 28, 72),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      h3: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 5, 24, 56),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      h4: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 4, 21, 44),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      h5: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 2.5, 18, 34),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      h6: {
        fontFamily: headingFamily,
        fontSize: scaledHeadingSize(baseFontSize, scaleRatio, 1.3, 16, 28),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.headingLineHeight,
        letterSpacing,
      },
      button: {
        fontFamily: bodyFamily,
        fontSize: pxToRem(baseFontSize * 0.875),
        fontWeight: typography.fontWeightMedium,
        lineHeight: typography.lineHeight,
        textTransform: "none",
        letterSpacing,
      },
      overline: {
        fontFamily: bodyFamily,
        fontSize: pxToRem(baseFontSize * 0.75),
        fontWeight: typography.fontWeightBold,
        lineHeight: typography.lineHeight,
        letterSpacing,
      },
      caption: {
        fontFamily: bodyFamily,
        fontSize: pxToRem(baseFontSize * 0.75),
        lineHeight: typography.lineHeight,
        letterSpacing,
      },
    },
    shape: { borderRadius: borders.radiusMd },
    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1200, xl: 1880 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "html, body, #root": { minHeight: "100%" },
          body: {
            backgroundColor: colors.background,
            color: colors.text,
            fontFamily: bodyFamily,
            fontSize: `${baseFontSize}px`,
            lineHeight: typography.lineHeight,
          },
          "::selection": {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: buttonMinHeight,
            borderRadius: borders.radiusMd,
            paddingBlock: buttonPaddingY,
            paddingInline: buttonPaddingX,
            transition:
              "transform .2s ease, box-shadow .2s ease, background-color .2s ease",
            "&:focus-visible": { boxShadow: shadows.focus },
            "&:active": { transform: "scale(.98)" },
          },
          contained: {
            boxShadow: shadows.sm,
            "&:hover": {
              boxShadow: shadows.lg,
              transform: "translateY(-1px)",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: borders.radiusLg,
            border: borderRule,
            boxShadow: shadows.md,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: borders.radiusLg,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: borders.radiusMd,
            backgroundColor: colors.surface,
            transition: "box-shadow .2s ease, border-color .2s ease",
            "&.Mui-focused": { boxShadow: shadows.focus },
          },
          notchedOutline: {
            borderColor: borders.color,
            borderStyle: borders.style,
            borderWidth: borders.width,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: borders.color,
            borderStyle: borders.style,
            borderWidth: 0,
            borderBottomWidth: borders.width,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borders.radiusPill,
            fontWeight: typography.fontWeightMedium,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { boxShadow: shadows.lg },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { boxShadow: shadows.lg },
        },
      },
    },
  });
}
